import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Copy,
  CreditCard,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import axiosInstance from '@/lib/axios';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useCommunity } from '@/hooks/use-community';
import type {
  CommunityPaymentAccount,
  MembershipFeeBucket,
  MembershipFeeComponent,
  MembershipFeeKind,
  MembershipInstallmentItem,
  MembershipMemberType,
  MembershipPaymentMode,
} from '@/types/membership';
import {
  FEE_KIND_LABELS,
  formatMembershipDate,
  formatMembershipPrice,
  getInstallmentTotal,
  getMembershipExpiryLabel,
  ISSUED_MEMBERSHIP_STATUS_LABELS,
  MEMBER_TYPE_LABELS,
  PAYMENT_DECLARATION_STATUS_LABELS,
  PAYMENT_MODE_LABELS,
} from '@/types/membership';

type MembershipApiResponse = {
  paymentAccounts: CommunityPaymentAccount[];
  feeStructure: {
    id: string;
    name: string;
    applicableFrom: string;
    memberTypes: MembershipMemberType[];
  } | null;
  issuedMembership: {
    id: string;
    status: string;
    selectedFeeKind: MembershipFeeKind;
    membershipStartDate?: string | null;
    membershipEndDate?: string | null;
    isLifetime: boolean;
    notes?: string | null;
    memberTypeId: string;
    installmentPackageId?: string | null;
  } | null;
  paymentDeclarations: Array<{
    id: string;
    amountDeclared: number;
    amountAccepted?: number | null;
    outstandingBalance: number;
    status: keyof typeof PAYMENT_DECLARATION_STATUS_LABELS;
    paymentMode: MembershipPaymentMode;
    declaredAt: string;
    notes?: string | null;
  }>;
};

const FEE_KIND_PRIORITY: MembershipFeeKind[] = [
  'MEMBERSHIP',
  'ONE_TIME',
  'REGISTRATION',
  'RENEWAL',
];

const todayInputValue = () => new Date().toISOString().slice(0, 10);

function pickPrimaryFeeKind(kinds: MembershipFeeKind[]): MembershipFeeKind {
  for (const kind of FEE_KIND_PRIORITY) {
    if (kinds.includes(kind)) {
      return kind;
    }
  }
  return kinds[0];
}

function allocateDeclaration(
  amount: number,
  components: MembershipFeeComponent[],
  installments: MembershipInstallmentItem[]
) {
  const targets =
    installments.length > 0
      ? installments.flatMap((installment) =>
          installment.componentSplits.map((split) => ({
            feeComponentId: split.feeComponentId,
            installmentSplitId: split.id ?? null,
            targetAmount: split.amount,
          }))
        )
      : components.map((component) => ({
          feeComponentId: component.id,
          installmentSplitId: null,
          targetAmount: component.amount,
        }));

  let remaining = amount;
  return targets
    .map((target) => {
      const allocated = Math.max(Math.min(remaining, target.targetAmount), 0);
      remaining -= allocated;
      return {
        feeComponentId: target.feeComponentId,
        installmentSplitId: target.installmentSplitId,
        amount: allocated,
      };
    })
    .filter((allocation) => allocation.amount > 0);
}

async function copyText(value: string, label: string) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  } catch {
    toast.error(`Failed to copy ${label.toLowerCase()}`);
  }
}

export default function MembershipPage() {
  const { communityId } = useParams<{ communityId: string }>();
  const { community: communityPayload } = useCommunity(communityId);
  const community = communityPayload?.community;

  const [selectedMemberTypeId, setSelectedMemberTypeId] = useState('');
  const [selectedFeeKinds, setSelectedFeeKinds] = useState<MembershipFeeKind[]>([]);
  const [packageByFeeKind, setPackageByFeeKind] = useState<Partial<Record<MembershipFeeKind, string>>>(
    {}
  );
  const [paymentMode, setPaymentMode] = useState<MembershipPaymentMode>('MANUAL_BANK');
  const [amountDeclared, setAmountDeclared] = useState('');
  const [amountTouched, setAmountTouched] = useState(false);
  const [joinDate, setJoinDate] = useState(todayInputValue);
  const [paidAt, setPaidAt] = useState(todayInputValue);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, mutate, isLoading } = useSWR<MembershipApiResponse>(
    community?.isMembershipManagementEnabled && communityId
      ? `/api/communities/${communityId}/membership/my`
      : null,
    (url: string) => axiosInstance.get(url).then((res) => res.data)
  );

  const feeStructure = data?.feeStructure ?? null;
  const issuedMembership = data?.issuedMembership ?? null;
  const paymentDeclarations = data?.paymentDeclarations ?? [];
  const memberTypes = feeStructure?.memberTypes ?? [];

  const selectedMemberType =
    memberTypes.find((item) => item.id === selectedMemberTypeId) ??
    memberTypes.find((item) => item.id === issuedMembership?.memberTypeId) ??
    null;

  const feeBuckets = useMemo(
    () => selectedMemberType?.feeBuckets.filter((bucket) => bucket.isEnabled) ?? [],
    [selectedMemberType]
  );

  const selectedBuckets = useMemo(
    () => feeBuckets.filter((bucket) => selectedFeeKinds.includes(bucket.feeKind)),
    [feeBuckets, selectedFeeKinds]
  );

  const selectedInstallments = useMemo(() => {
    return selectedBuckets.flatMap((bucket) => {
      const packageId = packageByFeeKind[bucket.feeKind];
      const pkg =
        bucket.installmentPackages.find((item) => item.id === packageId) ??
        bucket.installmentPackages[0] ??
        null;
      return pkg?.installments[0] ? [pkg.installments[0]] : [];
    });
  }, [packageByFeeKind, selectedBuckets]);

  const selectedComponents = useMemo(
    () => selectedBuckets.flatMap((bucket) => bucket.components),
    [selectedBuckets]
  );

  const suggestedAmount = useMemo(() => {
    if (selectedInstallments.length > 0) {
      return selectedInstallments.reduce(
        (sum, installment) => sum + getInstallmentTotal(installment),
        0
      );
    }
    return selectedBuckets.reduce((sum, bucket) => sum + bucket.totalAmount, 0);
  }, [selectedBuckets, selectedInstallments]);

  const paymentAccountsForMode = useMemo(() => {
    const accountIds = new Set(selectedComponents.map((component) => component.paymentAccountId));
    return (data?.paymentAccounts ?? []).filter((account) => {
      if (!accountIds.has(account.id) || !account.isActive) {
        return false;
      }
      return paymentMode === 'MANUAL_BANK' ? account.type === 'BANK' : account.type === 'UPI';
    });
  }, [data?.paymentAccounts, paymentMode, selectedComponents]);

  useEffect(() => {
    if (issuedMembership?.memberTypeId) {
      setSelectedMemberTypeId(issuedMembership.memberTypeId);
      return;
    }
    if (memberTypes.length === 1) {
      setSelectedMemberTypeId(memberTypes[0].id);
    }
  }, [issuedMembership?.memberTypeId, memberTypes]);

  useEffect(() => {
    if (feeBuckets.length === 0) {
      setSelectedFeeKinds([]);
      return;
    }

    setSelectedFeeKinds((current) => {
      const stillValid = current.filter((kind) =>
        feeBuckets.some((bucket) => bucket.feeKind === kind)
      );
      if (stillValid.length > 0) {
        return stillValid;
      }
      if (issuedMembership?.selectedFeeKind) {
        const match = feeBuckets.find(
          (bucket) => bucket.feeKind === issuedMembership.selectedFeeKind
        );
        if (match) {
          return [match.feeKind];
        }
      }
      if (feeBuckets.length === 1) {
        return [feeBuckets[0].feeKind];
      }
      return [];
    });
  }, [feeBuckets, issuedMembership?.selectedFeeKind]);

  useEffect(() => {
    setPackageByFeeKind((current) => {
      const next: Partial<Record<MembershipFeeKind, string>> = {};
      for (const bucket of selectedBuckets) {
        const packages = bucket.installmentPackages.filter((pkg) => pkg.id);
        if (packages.length === 0) {
          continue;
        }
        const existing = current[bucket.feeKind];
        if (existing && packages.some((pkg) => pkg.id === existing)) {
          next[bucket.feeKind] = existing;
        } else if (packages.length === 1 && packages[0].id) {
          next[bucket.feeKind] = packages[0].id;
        } else if (
          issuedMembership?.installmentPackageId &&
          packages.some((pkg) => pkg.id === issuedMembership.installmentPackageId)
        ) {
          next[bucket.feeKind] = issuedMembership.installmentPackageId;
        }
      }
      return next;
    });
  }, [issuedMembership?.installmentPackageId, selectedBuckets]);

  useEffect(() => {
    if (!amountTouched) {
      setAmountDeclared(suggestedAmount > 0 ? String(suggestedAmount) : '');
    }
  }, [amountTouched, suggestedAmount]);

  const toggleFeeKind = (feeKind: MembershipFeeKind) => {
    setSelectedFeeKinds((current) =>
      current.includes(feeKind)
        ? current.filter((kind) => kind !== feeKind)
        : [...current, feeKind]
    );
    setAmountTouched(false);
  };

  const declarePayment = async () => {
    if (!communityId || !selectedMemberType || selectedBuckets.length === 0 || !amountDeclared) {
      toast.error('Select a member type, at least one fee, and a declared amount');
      return;
    }

    setIsSubmitting(true);
    try {
      let membershipId = issuedMembership?.id;
      const primaryFeeKind = pickPrimaryFeeKind(selectedFeeKinds);
      const primaryPackageId = packageByFeeKind[primaryFeeKind] ?? null;

      if (!membershipId) {
        const createMembershipResponse = await axiosInstance.post(
          `/api/communities/${communityId}/membership/my`,
          {
            memberTypeId: selectedMemberType.id,
            selectedFeeKind: primaryFeeKind,
            installmentPackageId: primaryPackageId,
            joinDate,
            membershipStartDate: joinDate,
            membershipEndDate: null,
            notes: notes || null,
          }
        );
        membershipId = createMembershipResponse.data.issuedMembership.id;
      }

      const numericAmount = Number(amountDeclared) || 0;
      const installmentItemId =
        selectedInstallments.length === 1 ? (selectedInstallments[0].id ?? null) : null;

      await axiosInstance.post(`/api/communities/${communityId}/membership/payment-declarations`, {
        issuedMembershipId: membershipId,
        installmentItemId,
        paymentMode,
        amountDeclared: numericAmount,
        paidAt: paidAt ? new Date(`${paidAt}T00:00:00.000Z`).toISOString() : null,
        referenceNumber: referenceNumber || null,
        notes: notes || null,
        allocations: allocateDeclaration(
          numericAmount,
          selectedComponents,
          selectedInstallments
        ),
      });

      toast.success('Payment declaration submitted');
      setAmountDeclared('');
      setAmountTouched(false);
      setReferenceNumber('');
      setNotes('');
      setPaidAt(todayInputValue());
      await mutate();
    } catch (error) {
      const maybeResponse = error as { response?: { data?: { error?: string } } };
      toast.error(maybeResponse.response?.data?.error ?? 'Failed to declare payment');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!communityId) return null;

  if (!community?.isMembershipManagementEnabled) {
    return (
      <div className="container mx-auto py-16 px-4 max-w-2xl text-center">
        <CreditCard className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h1 className="text-2xl font-bold">Membership</h1>
        <p className="text-muted-foreground mt-2">
          Membership management is not enabled for this community.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto py-16 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const memberTypeLocked = Boolean(issuedMembership?.memberTypeId);
  const showMemberTypePicker = memberTypes.length > 1 && !memberTypeLocked;

  return (
    <div className="container mx-auto py-10 px-4 max-w-4xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Membership</h1>
        <p className="text-muted-foreground mt-2">
          Choose your member type and fees, pay using the account details below, then declare your
          transfer.
        </p>
      </div>

      {issuedMembership ? (
        <Card className="border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {issuedMembership.status === 'ACTIVE' ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-amber-500" />
              )}
              Current Membership
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Badge>
                {
                  ISSUED_MEMBERSHIP_STATUS_LABELS[
                    issuedMembership.status as keyof typeof ISSUED_MEMBERSHIP_STATUS_LABELS
                  ]
                }
              </Badge>
              <Badge variant="secondary">{FEE_KIND_LABELS[issuedMembership.selectedFeeKind]}</Badge>
              {selectedMemberType && (
                <Badge variant="outline">
                  {selectedMemberType.name || MEMBER_TYPE_LABELS[selectedMemberType.feeType]}
                </Badge>
              )}
              {issuedMembership.isLifetime && <Badge variant="outline">Lifetime</Badge>}
            </div>
            {issuedMembership.membershipStartDate && (
              <p className="text-sm text-muted-foreground">
                Membership started: {formatMembershipDate(issuedMembership.membershipStartDate)}
              </p>
            )}
            {issuedMembership.membershipEndDate && (
              <p className="text-sm text-muted-foreground">
                {getMembershipExpiryLabel(issuedMembership.membershipEndDate)}
              </p>
            )}
            {issuedMembership.notes && (
              <p className="text-sm text-muted-foreground">Notes: {issuedMembership.notes}</p>
            )}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Declare Membership Payment</CardTitle>
          <CardDescription>
            Select every fee you are paying now. Payment details update based on your selection.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <section className="space-y-3">
            <Label>Member type</Label>
            {showMemberTypePicker ? (
              <div className="grid gap-3 sm:grid-cols-2">
                {memberTypes.map((memberType) => {
                  const selected = selectedMemberType?.id === memberType.id;
                  return (
                    <ChoiceBlock
                      key={memberType.id}
                      selected={selected}
                      title={memberType.name || MEMBER_TYPE_LABELS[memberType.feeType]}
                      description={MEMBER_TYPE_LABELS[memberType.feeType]}
                      onClick={() => {
                        setSelectedMemberTypeId(memberType.id);
                        setAmountTouched(false);
                      }}
                    />
                  );
                })}
              </div>
            ) : selectedMemberType ? (
              <HiddenSingleChoice
                label={selectedMemberType.name || MEMBER_TYPE_LABELS[selectedMemberType.feeType]}
                hint={MEMBER_TYPE_LABELS[selectedMemberType.feeType]}
              />
            ) : (
              <p className="text-sm text-muted-foreground">No member types available.</p>
            )}
          </section>

          {selectedMemberType && (
            <section className="space-y-3">
              <div>
                <Label>Fees to pay</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  You can select more than one fee when paying together.
                </p>
              </div>
              {feeBuckets.length === 0 ? (
                <p className="text-sm text-muted-foreground">No payable fees for this member type.</p>
              ) : feeBuckets.length === 1 ? (
                <HiddenSingleChoice
                  label={FEE_KIND_LABELS[feeBuckets[0].feeKind]}
                  hint={formatMembershipPrice(feeBuckets[0].totalAmount)}
                />
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  {feeBuckets.map((bucket) => {
                    const selected = selectedFeeKinds.includes(bucket.feeKind);
                    return (
                      <ChoiceBlock
                        key={bucket.feeKind}
                        selected={selected}
                        title={FEE_KIND_LABELS[bucket.feeKind]}
                        description={formatMembershipPrice(bucket.totalAmount)}
                        onClick={() => toggleFeeKind(bucket.feeKind)}
                      />
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {selectedBuckets.map((bucket) => (
            <SelectedBucketSection
              key={bucket.feeKind}
              bucket={bucket}
              selectedPackageId={packageByFeeKind[bucket.feeKind] ?? ''}
              onSelectPackage={(packageId) =>
                setPackageByFeeKind((current) => ({
                  ...current,
                  [bucket.feeKind]: packageId,
                }))
              }
            />
          ))}

          {selectedBuckets.length > 0 && (
            <>
              <section className="space-y-3">
                <Label>Payment method</Label>
                <div className="grid gap-3 sm:grid-cols-2">
                  <ChoiceBlock
                    selected={paymentMode === 'MANUAL_BANK'}
                    title="Bank transfer"
                    description="Pay to the bank accounts below"
                    onClick={() => setPaymentMode('MANUAL_BANK')}
                  />
                  <ChoiceBlock
                    selected={paymentMode === 'MANUAL_UPI'}
                    title="UPI"
                    description="Pay using the UPI details below"
                    onClick={() => setPaymentMode('MANUAL_UPI')}
                  />
                </div>
              </section>

              <section className="space-y-3">
                <Label>
                  {paymentMode === 'MANUAL_BANK' ? 'Bank account details' : 'UPI payment details'}
                </Label>
                {paymentAccountsForMode.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No {paymentMode === 'MANUAL_BANK' ? 'bank' : 'UPI'} accounts are linked to the
                    selected fees. Choose the other payment method or ask an admin to add accounts.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {paymentAccountsForMode.map((account) => (
                      <PaymentAccountCard key={account.id} account={account} />
                    ))}
                  </div>
                )}
              </section>

              <div className="grid gap-4 md:grid-cols-2">
                {!issuedMembership && (
                  <div className="space-y-2">
                    <Label>Join date</Label>
                    <Input
                      type="date"
                      value={joinDate}
                      onChange={(e) => setJoinDate(e.target.value)}
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Payment date</Label>
                  <Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Declared amount</Label>
                  <Input
                    type="number"
                    min={0}
                    inputMode="numeric"
                    placeholder="0"
                    value={amountDeclared}
                    onChange={(e) => {
                      setAmountTouched(true);
                      setAmountDeclared(e.target.value);
                    }}
                  />
                  {suggestedAmount > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Suggested total for selection: {formatMembershipPrice(suggestedAmount)}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Reference number</Label>
                  <Input
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    placeholder="UTR / transaction ID"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
              </div>

              <Button onClick={declarePayment} disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Declare payment
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Payment History</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {paymentDeclarations.length === 0 ? (
            <p className="text-sm text-muted-foreground">No payment declarations yet.</p>
          ) : (
            paymentDeclarations.map((declaration) => (
              <div key={declaration.id} className="rounded-xl border p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">
                    {PAYMENT_DECLARATION_STATUS_LABELS[declaration.status]}
                  </Badge>
                  <Badge>{PAYMENT_MODE_LABELS[declaration.paymentMode]}</Badge>
                </div>
                <p className="mt-2 text-sm">
                  Declared {formatMembershipPrice(declaration.amountDeclared)}
                  {declaration.amountAccepted != null
                    ? ` • accepted ${formatMembershipPrice(declaration.amountAccepted)}`
                    : ''}
                  {declaration.outstandingBalance > 0
                    ? ` • balance ${formatMembershipPrice(declaration.outstandingBalance)}`
                    : ''}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Submitted {new Date(declaration.declaredAt).toLocaleString()}
                </p>
                {declaration.notes && (
                  <p className="text-sm text-muted-foreground mt-1">{declaration.notes}</p>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ChoiceBlock({
  selected,
  title,
  description,
  onClick,
}: {
  selected: boolean;
  title: string;
  description?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-xl border p-4 text-left transition-colors',
        selected
          ? 'border-primary bg-primary/5 shadow-sm'
          : 'hover:border-primary/40 hover:bg-muted/30'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-medium">{title}</p>
          {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
        </div>
        <span
          className={cn(
            'mt-0.5 flex h-5 w-5 items-center justify-center rounded-full border',
            selected ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/30'
          )}
        >
          {selected && <Check className="h-3 w-3" />}
        </span>
      </div>
    </button>
  );
}

function HiddenSingleChoice({ label, hint }: { label: string; hint?: string }) {
  return (
    <div className="rounded-xl border bg-muted/20 px-4 py-3">
      <p className="font-medium">{label}</p>
      {hint && <p className="text-sm text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

function SelectedBucketSection({
  bucket,
  selectedPackageId,
  onSelectPackage,
}: {
  bucket: MembershipFeeBucket;
  selectedPackageId: string;
  onSelectPackage: (packageId: string) => void;
}) {
  const packages = bucket.installmentPackages.filter((pkg) => pkg.id);
  const selectedPackage =
    packages.find((pkg) => pkg.id === selectedPackageId) ?? packages[0] ?? null;

  return (
    <section className="rounded-xl border p-4 space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge>{FEE_KIND_LABELS[bucket.feeKind]}</Badge>
        <Badge variant="outline">{formatMembershipPrice(bucket.totalAmount)}</Badge>
      </div>

      {packages.length > 1 ? (
        <div className="space-y-3">
          <Label>Installment package</Label>
          <div className="grid gap-3 sm:grid-cols-2">
            {packages.map((pkg) => (
              <ChoiceBlock
                key={pkg.id}
                selected={selectedPackage?.id === pkg.id}
                title={pkg.name}
                description={pkg.description || undefined}
                onClick={() => pkg.id && onSelectPackage(pkg.id)}
              />
            ))}
          </div>
        </div>
      ) : packages.length === 1 ? (
        <HiddenSingleChoice
          label={packages[0].name}
          hint={packages[0].description || 'Installment package'}
        />
      ) : null}

      {selectedPackage && selectedPackage.installments.length > 1 && (
        <div className="space-y-2">
          {selectedPackage.installments.map((installment) => (
            <div key={installment.id} className="rounded-lg border p-3 text-sm">
              <div className="font-medium">
                {installment.name}: {formatMembershipPrice(getInstallmentTotal(installment))}
              </div>
              <div className="text-muted-foreground mt-1">
                Payable until {formatMembershipDate(installment.payableEndDate)}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function PaymentAccountCard({ account }: { account: CommunityPaymentAccount }) {
  if (account.type === 'BANK') {
    const rows = [
      { label: 'Account name', value: account.name },
      { label: 'Beneficiary', value: account.beneficiaryName },
      { label: 'Bank', value: account.bankName },
      { label: 'Account number', value: account.accountNumber },
      { label: 'IFSC', value: account.ifscCode },
      { label: 'Branch', value: account.branchName },
    ].filter((row): row is { label: string; value: string } => Boolean(row.value));

    const copyAll = [
      account.name,
      account.beneficiaryName ? `Beneficiary: ${account.beneficiaryName}` : null,
      account.bankName ? `Bank: ${account.bankName}` : null,
      account.accountNumber ? `Account: ${account.accountNumber}` : null,
      account.ifscCode ? `IFSC: ${account.ifscCode}` : null,
      account.branchName ? `Branch: ${account.branchName}` : null,
    ]
      .filter(Boolean)
      .join('\n');

    return (
      <div className="rounded-xl border p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="font-medium">{account.name}</p>
            <p className="text-sm text-muted-foreground">Bank transfer</p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => copyText(copyAll, 'Bank details')}>
            <Copy className="mr-2 h-4 w-4" />
            Copy all
          </Button>
        </div>
        <div className="space-y-2">
          {rows.map((row) => (
            <CopyRow key={row.label} label={row.label} value={row.value} />
          ))}
        </div>
      </div>
    );
  }

  const rows = [
    { label: 'Account name', value: account.name },
    { label: 'UPI ID', value: account.upiId },
    { label: 'UPI name', value: account.upiName },
    { label: 'UPI type', value: account.upiType },
    { label: 'UPI account number', value: account.upiAccountNumber },
  ].filter((row): row is { label: string; value: string } => Boolean(row.value));

  return (
    <div className="rounded-xl border p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-medium">{account.name}</p>
          <p className="text-sm text-muted-foreground">UPI</p>
        </div>
        {account.upiId && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => copyText(account.upiId!, 'UPI ID')}
          >
            <Copy className="mr-2 h-4 w-4" />
            Copy UPI ID
          </Button>
        )}
      </div>
      <div className="space-y-2">
        {rows.map((row) => (
          <CopyRow key={row.label} label={row.label} value={row.value} />
        ))}
      </div>
      {account.upiQrCode && (
        <div className="rounded-lg border bg-muted/20 p-3">
          <p className="text-sm font-medium mb-2">QR code</p>
          <img
            src={account.upiQrCode}
            alt={`${account.name} UPI QR`}
            className="mx-auto max-h-48 rounded-lg"
          />
        </div>
      )}
    </div>
  );
}

function CopyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg bg-muted/30 px-3 py-2">
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium break-all">{value}</p>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="shrink-0"
        onClick={() => copyText(value, label)}
      >
        <Copy className="h-4 w-4" />
      </Button>
    </div>
  );
}
